import { DIRECTIONS, dx, dy, opposite, turnLeft, turnRight, type Direction } from './direction';
import { needsMowing, type Grid } from './grid';

/** Boss: Rival Robo-Mower. The neighbor's robot, as the sim drives it. */
export interface RivalState {
  x: number;
  y: number;
  dir: Direction;
  /** Ticks into crossing to the next tile (0: at a tile center). */
  progress: number;
  /** Grass tiles it has mowed. */
  tiles: number;
}

/**
 * The robot's next heading from its tile: toward the nearest grass still to mow (keeping straight
 * on when that is as good, so it stripes in rows), never onto a tile `blocked` says is taken.
 * Null when there is nothing reachable to mow, or it is boxed in for now.
 */
export function rivalHeading(
  grid: Grid,
  r: RivalState,
  passable: (x: number, y: number) => boolean,
): Direction | null {
  const w = grid.width;
  const order = [r.dir, turnLeft(r.dir), turnRight(r.dir), opposite(r.dir)];
  const first = new Map<number, Direction>();
  const startKey = r.y * w + r.x;
  const seen = new Set<number>([startKey]);
  const queue: { x: number; y: number }[] = [{ x: r.x, y: r.y }];
  for (let head = 0; head < queue.length; head++) {
    const p = queue[head]!;
    const here = p.y * w + p.x;
    for (const d of here === startKey ? order : DIRECTIONS) {
      const nx = p.x + dx(d);
      const ny = p.y + dy(d);
      const n = ny * w + nx;
      if (seen.has(n) || !passable(nx, ny)) continue;
      seen.add(n);
      const step = here === startKey ? d : first.get(here)!;
      if (needsMowing(grid.tileAt(nx, ny))) return step;
      first.set(n, step);
      queue.push({ x: nx, y: ny });
    }
  }
  return null;
}
